import { useCallback, useRef } from 'react';
import moment from 'moment';
import { Link, useNavigate } from 'react-router';
import { SectionTitle } from '../../components/typography/Heading';
import ExpandableTimelineItem from '../project-timeline/ExpandableTimelineItem';
import { canUserEditProject, isUserAndReceiverSame } from '../../user/user-selectors';
import { USER_TYPE } from '../../constants';
import { formatVerifiedOwnerName } from '../../user/user-model';
import ProfileInfoForUpdateReceiver from './update-receiver/profile-info-for-update-receiver/ProfileInfoForUpdateReceiver';
import RoundedButtonPrimary from '../../components/button/rounded-button-primary/RoundedButtonPrimary';
import ScreenReaderOnly from '../../accessibility/ScreenReaderOnly';
import { useUser } from '../../user/useUser';
import { useProject } from '../project-hooks/useProject';
import { useSelector } from 'react-redux';
import { getUpdatesForProjectId } from '../project-update-reducer';
import { systemDateToHumanReadableDateWithWithClockFormat } from '../../utils';
import styles from './project-update.module.less';

const ProjectUpdateTitle = ({ title, canEdit, editLink }) => {
    return (
        <SectionTitle>
            {title}
            {canEdit && (
                <Link to={editLink} className={styles.editIcon}>
                    <i className={`fa-regular fa-pen-to-square`} />
                    <ScreenReaderOnly>Endre tittel</ScreenReaderOnly>
                </Link>
            )}
        </SectionTitle>
    );
};

const ReceiverUpdateTitle = ({ receiverInfo, userIsReceiverAndCanUpdate, editLink }) => {
    return (
        <div className={styles.receiverUpdateTitleContainer}>
            <ProfileInfoForUpdateReceiver {...receiverInfo} />
            {userIsReceiverAndCanUpdate && (
                <Link to={editLink} className={styles.receiverEditIcon}>
                    <i className={`fa-regular fa-pen-to-square`} />
                    <ScreenReaderOnly>Endre tittel</ScreenReaderOnly>
                </Link>
            )}
        </div>
    );
};

const getBadgeTitle = (user) => {
    if (user.type === USER_TYPE.USER) {
        return user.verified ? 'Identifisert' : 'Ikke identifisert ennå';
    }
};

const ProjectUpdateList = ({ className }) => {
    const user = useUser();
    const project = useProject();
    const projectId = project.id;
    const receiver = project.receiver;
    const navigate = useNavigate();
    const projectUpdates = useSelector((state) => getUpdatesForProjectId(state, projectId));
    projectUpdates.sort((a, b) => moment(b.created_at).valueOf() - moment(a.created_at).valueOf());
    const userCanEditProject = canUserEditProject(user, project.id);
    const userIsReceiverAndCanUpdate = isUserAndReceiverSame(user, receiver);
    const containerElement = useRef(null);

    const getReceiverInfo = useCallback(() => {
        if (!receiver) {
            return {};
        }
        const isOrgUser = receiver.type === USER_TYPE.ORG;

        let name;
        let verifiedName;
        let imageIsEditable = false;
        let organizationNumber = null;

        if (isOrgUser) {
            name = receiver.name;
            verifiedName = receiver.name;
            organizationNumber = !receiver?.profile?.hideOrgNumber && receiver.organization_number;
        } else {
            name = `${receiver.first} ${receiver.last}`;
            verifiedName = formatVerifiedOwnerName(receiver.verified_name);
            imageIsEditable = receiver.id === user.id;
        }

        return {
            name: verifiedName || name,
            title: verifiedName || name,
            hideBadge: isOrgUser && !receiver.verified,
            verified: receiver.verified,
            badgeTitle: getBadgeTitle(receiver),
            imageSrc: receiver.img_url,
            hideImage: !receiver.img_url,
            hideLogo: !receiver.img_url,
            isEditable: imageIsEditable,
            urlForOrganizationNavigation: isOrgUser ? `/org/${receiver?.id}` : null,
            showSpeakWithOwner: false,
            organizationNumber,
            isOrganization: isOrgUser,
        };
    }, [receiver?.id, user?.id]);

    const receiverInfo = getReceiverInfo();

    const updatebaseUrl = `/project/${projectId}/update`;
    const receiverUpdateBaseUrl = `/project/${projectId}/receiver-update`;

    return (
        <div ref={containerElement} className={className}>
            {projectUpdates.length === 0 && <p>Her er det ingen oppdateringer ennå 📢</p>}
            {userCanEditProject && (
                <>
                    <p>
                        Oppdater giverne dine om hvordan spleisen din går. Det kan hjelpe til med å skape engasjement
                        rundt innsamlingen din.
                    </p>
                    <RoundedButtonPrimary className={styles.button} onClick={() => navigate(`${updatebaseUrl}/new`)}>
                        Skriv en oppdatering
                    </RoundedButtonPrimary>
                </>
            )}
            {!userCanEditProject && userIsReceiverAndCanUpdate && (
                <>
                    <p>Gi innsamler og givere en takk eller informer om nyheter eller spennende informasjon!</p>
                    <RoundedButtonPrimary
                        className={styles.button}
                        onClick={() => navigate(`${receiverUpdateBaseUrl}/new`)}
                    >
                        Skriv en takk eller oppdatering
                    </RoundedButtonPrimary>
                </>
            )}
            {projectUpdates.length > 0 && (
                <div>
                    <ol>
                        {projectUpdates.map((update) => {
                            const title = update.from_receiver ? (
                                <ReceiverUpdateTitle
                                    receiverInfo={receiverInfo}
                                    userIsReceiverAndCanUpdate={userIsReceiverAndCanUpdate}
                                    editLink={`${receiverUpdateBaseUrl}/${update.id}`}
                                />
                            ) : (
                                <ProjectUpdateTitle
                                    title={update.title}
                                    canEdit={userCanEditProject}
                                    editLink={`${updatebaseUrl}/${update.id}`}
                                />
                            );
                            return (
                                <ExpandableTimelineItem
                                    title={title}
                                    tag={'article'}
                                    key={update.created_at}
                                    id={update.id}
                                    subtitle={
                                        <span suppressHydrationWarning>
                                            {systemDateToHumanReadableDateWithWithClockFormat(update.created_at)}
                                        </span>
                                    }
                                    expandedInfo={update.description}
                                    canEdit={!!userCanEditProject}
                                    editLink={`${updatebaseUrl}/${update.id}`}
                                />
                            );
                        })}
                    </ol>
                </div>
            )}
        </div>
    );
};

export default ProjectUpdateList;
